import type { QuotaKey } from '@/core/authz/entitlement-catalog';
import { useEntitlements } from '@/core/hooks/use-entitlements';

interface UseQuotaResult {
  limit: number | undefined;
  current: number;
  remaining: number | null;
  isUnlimited: boolean;
  isAtLimit: boolean;
  isOverLimit: boolean;
  isLoading: boolean;
}

function resolveUsageCurrent(
  quotaKey: QuotaKey,
  usage: ReturnType<typeof useEntitlements>['usage'],
): number {
  if (!usage) return 0;
  if (quotaKey === 'quota.members') {
    return usage.members.active;
  }
  if (quotaKey === 'quota.included_reputation_locations') {
    return usage.reputationLocations.connected;
  }
  if (quotaKey === 'quota.feedback_invite_recipients') {
    return usage.feedbackInviteRecipients?.used ?? 0;
  }
  return 0;
}

export function useQuota(quotaKey: QuotaKey): UseQuotaResult {
  const { getQuota, usage, isLoading } = useEntitlements();

  const limit = getQuota(quotaKey);
  const current = resolveUsageCurrent(quotaKey, usage);
  const isUnlimited = limit === -1;
  const effectiveLimit = isUnlimited ? undefined : limit;
  const remaining =
    effectiveLimit === undefined ? null : Math.max(0, effectiveLimit - current);
  const isAtLimit =
    !isLoading &&
    !isUnlimited &&
    effectiveLimit !== undefined &&
    current >= effectiveLimit;
  const isOverLimit =
    !isLoading &&
    !isUnlimited &&
    effectiveLimit !== undefined &&
    current > effectiveLimit;

  return {
    limit,
    current,
    remaining,
    isUnlimited,
    isAtLimit,
    isOverLimit,
    isLoading,
  };
}
